import * as React from 'react';

import { cn } from '@/lib/utils';
import type { FamilleNode } from '@/routes/supplier/catalog/catalog-referential-model';

// =============================================================================
// family-nav.tsx — MENU LATÉRAL GAUCHE : navigation par FAMILLES / SOUS-FAMILLES (DBU §3.4).
// =============================================================================
// DBU §3.4 l.2686/2698 : « un menu latéral gauche permettant de naviguer par familles et
// sous-familles de profils ». Consultation seule : la sélection est un ÉTAT D'URL (search params
// `famille` / `sousFamille` ; RULE_REF_006 : jamais localStorage), la nav délègue aux callbacks.
//
// Sélectionner une famille l'ouvre (affiche ses sous-familles) et pose le filtre `famille` ;
// sélectionner une sous-famille AFFINE (pose `sousFamille`). « Toutes les familles » réinitialise
// la portée.
//
// WCAG 2.1 AA : <nav aria-label>, chaque entrée est un <button> natif (navigation clavier),
// `aria-current` porte la sélection (jamais une couleur seule), focus visible homogène (ring).

export interface FamilyNavProps {
  /** Arborescence familles → sous-familles (issue de `buildFamilyTree`). */
  readonly familles: readonly FamilleNode[];
  /** Famille filtrée (clé d'URL) ; `undefined` = toutes. */
  readonly selectedFamille: string | undefined;
  /** Sous-famille filtrée (clé d'URL) ; `undefined` = toutes les sous-familles de la famille. */
  readonly selectedSousFamille: string | undefined;
  /** Sélectionne une famille (`undefined` = toutes). Réinitialise la sous-famille (porté par le parent). */
  readonly onSelectFamille: (famille: string | undefined) => void;
  /** Sélectionne une sous-famille (`undefined` = toutes les sous-familles de la famille courante). */
  readonly onSelectSousFamille: (sousFamille: string | undefined) => void;
}

/** Libellé « N profil(s) » (accord grammatical ; le compteur est toujours textuel). */
function countLabel(count: number): string {
  return `${String(count)} profil${count > 1 ? 's' : ''}`;
}

/** Un bouton de navigation (famille ou sous-famille), avec état sélectionné accessible. */
function NavButton({
  label,
  count,
  active,
  indent,
  onSelect,
}: {
  readonly label: string;
  readonly count: number;
  readonly active: boolean;
  readonly indent: boolean;
  readonly onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={active ? 'true' : undefined}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        indent && 'pl-6 text-[0.8125rem]',
        active
          ? 'bg-accent font-medium text-accent-foreground'
          : 'text-foreground hover:bg-muted',
      )}
    >
      <span className="truncate">{label}</span>
      <span
        className="shrink-0 tabular-nums text-xs text-muted-foreground"
        aria-label={countLabel(count)}
      >
        {count}
      </span>
    </button>
  );
}

export function FamilyNav({
  familles,
  selectedFamille,
  selectedSousFamille,
  onSelectFamille,
  onSelectSousFamille,
}: FamilyNavProps): React.JSX.Element {
  const total = familles.reduce((sum, famille) => sum + famille.count, 0);
  return (
    <nav aria-label="Familles et sous-familles de profils" className="flex flex-col gap-1">
      <p className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Familles de profils
      </p>
      <NavButton
        label="Toutes les familles"
        count={total}
        active={selectedFamille === undefined}
        indent={false}
        onSelect={() => onSelectFamille(undefined)}
      />
      {familles.map((famille) => {
        const familleActive = selectedFamille === famille.key;
        return (
          <div key={famille.key} className="flex flex-col gap-0.5">
            <NavButton
              label={famille.key}
              count={famille.count}
              active={familleActive && selectedSousFamille === undefined}
              indent={false}
              onSelect={() => onSelectFamille(famille.key)}
            />
            {/* Sous-familles visibles uniquement quand la famille est ouverte (sélectionnée). */}
            {familleActive && famille.sousFamilles.length > 0 ? (
              <div className="flex flex-col gap-0.5">
                {famille.sousFamilles.map((sousFamille) => (
                  <NavButton
                    key={sousFamille.key}
                    label={sousFamille.key}
                    count={sousFamille.count}
                    active={selectedSousFamille === sousFamille.key}
                    indent
                    onSelect={() => onSelectSousFamille(sousFamille.key)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
